import React, { useMemo, useState, useContext, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { XIcon } from '@heroicons/react/outline'
import { topBarStore } from '../../../lib/stores/TopBarStore'
import deviceIsMobile from '../../../lib/util/device-is-mobile'
import constants from '../../../lib/constants'
import ServerError from '../../../lib/components/ServerError'
import { caseDetailUrl, HOME } from '../../routing/paths'
import StoreContext from '../../providers/storesContext'
import InputWrapper from '../../components/Forms/InputWrapper'
import FormSelect from '../../components/Forms/FormSelect'
import Button from '../../components/Button'
import ManualSearchStore from './ManualSearchStore'
import c from 'classnames'
import config from '../../../lib/config'


const ManualSearch = () => {
  const history = useHistory()
  const { area } = useParams()
  const { caseDetailStore } = useContext(StoreContext)
  const [manualSearchStore] = useState(() => new ManualSearchStore())

  const options = useMemo(
    () =>
      Object.entries(constants.states).map(([code, state]) => ({
        value: code,
        label: state
      })),
    []
  )

  const handleChangeCaseNumber = (val) => manualSearchStore.changeCaseNumber(val)
  const handleChangeLicensePlateNumber = (val) => manualSearchStore.changeLicensePlateNumber(val.toUpperCase())
  const handleChangeLicensePlateState = (e) => {
    if (e.target.value != '') {
      manualSearchStore.changeLicensePlateState(e.target.value)
    }
  }

  const handleSearch = () => {
    getRecaptchaToken().then((token) => {
      manualSearchStore.search(token).then((immobilizationCase) => {
        if (immobilizationCase !== false) {
          caseDetailStore.setCase(immobilizationCase)
          caseDetailStore.setBillsLoaded(true)
          history.push(caseDetailUrl(immobilizationCase))
        }
      })
    })
  }

  const getRecaptchaToken = async() => {
    if(!config.GOOGLE_RECAPTCHA_ENABLED){
      return null;
    }
   const result =  await new Promise((res, rej) => {
    window.grecaptcha.ready(() => window.grecaptcha.execute(config.GOOGLE_RECAPTCHA_SITE_KEY, { action: 'submit' }).then(
      (token) => res(token),
    ))
  })
    return result;
  }

  useEffect(async () => {
    await manualSearchStore.loadAreas()
    if (area) {
      topBarStore.setHideLogo(true)
      fillArea()
    }
    

    if (deviceIsMobile()) {
      const action = (
        <Link to={HOME}>
          <XIcon className="h-6 w-6" />
        </Link>
      )

      topBarStore.setActionIcon(action)
    }

    return () => {
      topBarStore.reset()
    }
  }, [])

  const fillArea = () => {
    const areaSelected = manualSearchStore.areas.filter((mappedArea) => {
      const currentAreaSlug = mappedArea.name?.toLowerCase().replaceAll(' ','-')
      return currentAreaSlug === area.toLowerCase()
    })[0];
    if (!areaSelected) {
      return history.push(HOME)
    }
    manualSearchStore.changeArea(areaSelected);
  }

  const { caseNumber, licensePlateNumber, licensePlateState, serverError, isLoading } =
    manualSearchStore

  return (
    <>
      {manualSearchStore.area?.imageUrl && (
        <div className='flex flex-col items-center py-8'>
          <img src={manualSearchStore.area.imageUrl} alt={manualSearchStore.area.name} className="h-60"/>
        </div>)}
      <form
        className="grid gap-6"
        onSubmit={(e) => {
          e.preventDefault()
        handleSearch()
      }}
    >
      <InputWrapper
        placeholder="Type in the case number..."
        onChange={handleChangeCaseNumber}
        inputStore={caseNumber}
        label="Case number"
        type="number"
        min="0"
        onlyInteger
      />
      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-gray-100 px-2 text-sm text-gray-500">OR</span>
        </div>
      </div>
      <InputWrapper
        placeholder="Type in the license plate number..."
        onChange={handleChangeLicensePlateNumber}
        inputStore={licensePlateNumber}
        label="License plate number"
      />
      <label className="flex flex-col items-start flex-grow">
        <span className="text-sm font-bold mb-1 text-gray-400">License plate state</span>
        <select
          className={
            manualSearchStore.licensePlateState.error
              ? 'p-2.5 w-full rounded-md text-gray-400 bg-red-50 focus:ring-red-500'
              : 'bg-gray-50 p-2.5 w-full rounded-md text-gray-400'
          }
          style={{ outlineColor: '#D1D5DB' }}
          placeholder="Select the license plate state..."
          onChange={handleChangeLicensePlateState}
          value={licensePlateState.value}
        >
          <option value="">Select the license plate state...</option>
          {options.map((option) => {
            return (
              <option key={`state_${option.value}`} value={option.value}>
                {option.label}
              </option>
            )
          })}
        </select>
        {manualSearchStore.licensePlateState.errorMessage !== '' && (
          <div className="text-red-400 text-xs pt-1">
            {manualSearchStore.licensePlateState.errorMessage}
          </div>
        )}
      </label>
      <Button onClick={handleSearch} isLoading={isLoading}>
        Search
      </Button>
      {serverError !== '' && <ServerError message={serverError} />}
    </form>
    </>
  )
}

export default observer(ManualSearch)
